import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import Die2D from './Die2D';
import { Die } from '../types/game';
import { computeDicePositions } from '../utils/dicePositions';
import {
  TablePreset,
  TablePresetId,
  TABLE_PRESETS,
  getSavedTablePresetId,
  setSavedTablePresetId,
  subscribeTablePreset,
  resolveTablePreset,
  computeQuickKeepOptions,
  describeKeptPattern,
} from '../utils/tablePresets';
import { themeManager } from '../utils/themeManager';
import { useI18n } from '../i18n';

const OuterFrame = styled.div<{ $preset: TablePreset }>`
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto 18px;
  padding: 12px;
  border-radius: 26px;
  background: ${p => p.$preset.frameGradient};
  border: 2px solid ${p => p.$preset.frameBorder};
  box-shadow:
    0 18px 40px -10px rgba(0, 0, 0, 0.55),
    0 6px 16px rgba(0, 0, 0, 0.35),
    inset 0 2px 3px rgba(255, 255, 255, 0.22),
    inset 0 -3px 6px rgba(0, 0, 0, 0.6);
  color: ${p => p.$preset.textColor};
  transition: background 0.4s ease, border-color 0.4s ease;

  @media (min-width: 1400px) {
    max-width: 760px;
    padding: 14px;
    border-radius: 28px;
  }

  @media (min-width: 1920px) {
    max-width: 920px;
    padding: 16px;
    border-radius: 30px;
  }

  @media (max-width: 520px) {
    padding: 8px;
    border-radius: 20px;
  }
`;

const FrameHeaderBar = styled.div<{ $preset: TablePreset }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 6px 8px;
  font-size: 12px;
  color: ${p => p.$preset.mutedTextColor};
`;

const BoardBrandBadge = styled.div<{ $preset: TablePreset }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.03em;
  color: ${p => p.$preset.accentColor};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.65);
`;

const PresetPickerWrapper = styled.div`
  position: relative;
`;

const PresetTriggerBtn = styled.button<{ $preset: TablePreset }>`
  background: rgba(0, 0, 0, 0.38);
  color: ${p => p.$preset.textColor};
  border: 1px solid ${p => p.$preset.trayBorder};
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.14);
    border-color: ${p => p.$preset.accentColor};
  }

  &:focus-visible {
    outline: 2px solid ${p => p.$preset.accentColor};
    outline-offset: 2px;
  }
`;

const PresetDropdown = styled.div<{ $preset: TablePreset }>`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 60;
  min-width: 230px;
  background: rgba(14, 18, 26, 0.96);
  backdrop-filter: blur(14px);
  border: 1px solid ${p => p.$preset.frameBorder};
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.65);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PresetOptionBtn = styled.button<{ $active: boolean; $accent: string }>`
  background: ${p => (p.$active ? 'rgba(255, 255, 255, 0.14)' : 'transparent')};
  color: #fff;
  border: 1px solid ${p => (p.$active ? p.$accent : 'transparent')};
  border-radius: 10px;
  padding: 7px 10px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const InnerTableWell = styled.div<{ $preset: TablePreset; $heightPx: number }>`
  position: relative;
  width: 100%;
  height: ${p => p.$heightPx}px;
  border-radius: 18px;
  background: ${p => p.$preset.surfaceGradient};
  box-shadow: ${p => p.$preset.surfaceVignette};
  border: 1.5px solid rgba(0, 0, 0, 0.65);
  outline: 1px solid ${p => p.$preset.trayBorder};
  overflow: hidden;
  user-select: none;

  /* Micro-weave / surface grain */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 0),
      repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.025) 0px,
        rgba(255, 255, 255, 0.025) 2px,
        transparent 2px,
        transparent 4px
      );
    background-size: 20px 20px, 8px 8px;
    opacity: ${p => p.$preset.surfacePatternOpacity};
    pointer-events: none;
  }

  /* Studio overhead spotlight cone */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 50% 26%,
      rgba(255, 255, 255, 0.11) 0%,
      rgba(255, 255, 255, 0.02) 48%,
      rgba(0, 0, 0, 0.28) 100%
    );
    pointer-events: none;
  }
`;

const ArenaZoneHint = styled.div<{ $preset: TablePreset }>`
  position: absolute;
  top: 10px;
  left: 14px;
  right: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$preset.mutedTextColor};
  opacity: 0.82;
  pointer-events: none;
  z-index: 1;
`;

const KeepTrayDock = styled.div<{
  $preset: TablePreset;
  $topPx: number;
  $heightPx: number;
  $hasLocked: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${p => p.$topPx}px;
  height: ${p => p.$heightPx}px;
  background: ${p => p.$preset.trayBackground};
  border-top: 2px solid ${p => (p.$hasLocked ? p.$preset.slotLockedBorder : p.$preset.trayBorder)};
  box-shadow:
    inset 0 6px 14px rgba(0, 0, 0, 0.55),
    0 -4px 16px rgba(0, 0, 0, 0.35);
  z-index: 1;
  transition: border-color 0.3s ease;
`;

const KeepTrayHeader = styled.div<{ $preset: TablePreset }>`
  height: 30px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: ${p => p.$preset.textColor};
`;

const KeepTrayTitle = styled.span<{ $preset: TablePreset }>`
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${p => p.$preset.accentColor};
  white-space: nowrap;
`;

const KeepTraySummary = styled.span<{ $preset: TablePreset }>`
  font-size: 11px;
  color: ${p => p.$preset.mutedTextColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const KeepSlotSocket = styled.div<{
  $preset: TablePreset;
  $x: number;
  $y: number;
  $size: number;
  $isLocked: boolean;
  $canInteract: boolean;
}>`
  position: absolute;
  left: ${p => p.$x}px;
  top: ${p => p.$y}px;
  width: ${p => p.$size}px;
  height: ${p => p.$size}px;
  transform: translate(-50%, -50%);
  border-radius: 16px;
  background: ${p => (p.$isLocked ? p.$preset.slotLockedBg : p.$preset.slotEmptyBg)};
  border: 1.5px ${p => (p.$isLocked ? 'solid' : 'dashed')}
    ${p => (p.$isLocked ? p.$preset.slotLockedBorder : 'rgba(255, 255, 255, 0.18)')};
  box-shadow: ${p =>
    p.$isLocked ? p.$preset.slotLockedGlow : 'inset 0 2px 6px rgba(0, 0, 0, 0.55)'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: ${p => (p.$canInteract ? 'pointer' : 'default')};
  transition: all 0.25s ease;
  z-index: 2;

  &:hover {
    border-color: ${p =>
      p.$canInteract ? p.$preset.accentColor : 'rgba(255, 255, 255, 0.18)'};
  }
`;

const SlotNumberLabel = styled.span<{ $isLocked: boolean }>`
  font-size: 10px;
  font-weight: 700;
  color: ${p => (p.$isLocked ? 'transparent' : 'rgba(255, 255, 255, 0.35)')};
  pointer-events: none;
`;

const PositionedDieSlot = styled.div<{
  $x: number;
  $y: number;
  $rolling: boolean;
}>`
  position: absolute;
  left: ${p => p.$x}px;
  top: ${p => p.$y}px;
  transform: translate(-50%, -50%) scale(${p => (p.$rolling ? 1.06 : 1)});
  transition:
    left 0.44s cubic-bezier(0.22, 1, 0.36, 1),
    top 0.44s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.3s ease;
  will-change: left, top, transform;
  z-index: 5;
  pointer-events: auto;
`;

const QuickKeepBar = styled.div<{ $preset: TablePreset }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
  padding: 4px 2px 0;
`;

const QuickKeepPill = styled.button<{ $preset: TablePreset; $active?: boolean }>`
  background: ${p =>
    p.$active
      ? `linear-gradient(135deg, ${p.$preset.accentColor}, #4caf50)`
      : 'rgba(0, 0, 0, 0.42)'};
  color: ${p => (p.$active ? '#081c0e' : p.$preset.textColor)};
  border: 1px solid ${p => (p.$active ? '#ffffff' : p.$preset.trayBorder)};
  border-radius: 999px;
  padding: 5px 11px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.18s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);

  &:hover {
    transform: translateY(-1px);
    border-color: ${p => p.$preset.accentColor};
    background: ${p =>
      p.$active
        ? `linear-gradient(135deg, ${p.$preset.accentColor}, #66bb6a)`
        : 'rgba(255, 255, 255, 0.15)'};
  }

  &:active {
    transform: scale(0.97);
  }
`;

export interface DiceBoard3DProps {
  dice: Die[];
  isRolling: boolean;
  canInteract: boolean;
  onToggleDie: (dieId: number) => void;
  /** Optional batch setter for quick-keep buttons */
  onSetLockedIds?: (lockedIds: number[]) => void;
  onPointerDownDie?: (dieId: number) => void;
  onPointerUpDie?: (dieId: number) => void;
  onPointerLeaveDie?: (dieId: number) => void;
  /** Optional overlay inside the table (e.g. opponent turn indicator in online mode) */
  overlay?: React.ReactNode;
}

export const DiceBoard3D: React.FC<DiceBoard3DProps> = ({
  dice,
  isRolling,
  canInteract,
  onToggleDie,
  onSetLockedIds,
  onPointerDownDie,
  onPointerUpDie,
  onPointerLeaveDie,
  overlay,
}) => {
  const { locale } = useI18n();
  const wellRef = useRef<HTMLDivElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);
  const [presetId, setPresetId] = useState<TablePresetId>(() => getSavedTablePresetId());
  const [resolvedPreset, setResolvedPreset] = useState<TablePreset>(() =>
    resolveTablePreset(getSavedTablePresetId())
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rollSeed, setRollSeed] = useState(0);

  const count = dice.length || 5;
  const isMaxi = count >= 6;

  // Measure inner table width for responsive collision-free layout
  const [boardWidth, setBoardWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 540;
    return Math.min(570, Math.max(296, window.innerWidth - 64));
  });

  useEffect(() => {
    const updatePreset = () => {
      const id = getSavedTablePresetId();
      setPresetId(id);
      setResolvedPreset(resolveTablePreset(id));
    };
    const unsubPreset = subscribeTablePreset(updatePreset);
    const unsubTheme = themeManager.subscribe(updatePreset);
    return () => {
      unsubPreset();
      unsubTheme();
    };
  }, []);

  useEffect(() => {
    const el = wellRef.current;
    const measure = () => {
      const w = el?.clientWidth;
      if (w && w > 180) {
        setBoardWidth(w);
      } else if (typeof window !== 'undefined') {
        setBoardWidth(Math.min(570, Math.max(296, window.innerWidth - 64)));
      }
    };
    measure();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && el) {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Re-jitter rolling arena positions on each new roll
  useEffect(() => {
    if (isRolling) {
      setRollSeed(s => s + 1);
    }
  }, [isRolling]);

  // Close preset dropdown on outside click or Escape
  useEffect(() => {
    if (!pickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  // Calculate responsive geometry guaranteeing 0% overlap between all dice
  const layout = useMemo(() => {
    const isCompactWidth = boardWidth < 430;
    const trayHeaderH = 30;
    const slotPaddingX = isCompactWidth ? 6 : 12;
    const slotStep = (boardWidth - slotPaddingX * 2) / count;

    // Choose a single uniform dieSize that fits cleanly inside each Keep Tray slot
    // AND fits cleanly inside the upper Rolling Arena.
    const maxDieForSlot = Math.floor(slotStep - (isCompactWidth ? 4 : 8));
    const targetDie = isCompactWidth
      ? isMaxi
        ? 52
        : 60
      : boardWidth >= 680
        ? 98
        : isMaxi
          ? 80
          : 88;
    const dieSize = Math.max(44, Math.min(targetDie, maxDieForSlot));

    const traySlotsAreaH = dieSize + (isCompactWidth ? 18 : 26);
    const trayHeight = trayHeaderH + traySlotsAreaH;

    // Upper Rolling Arena height: enough for 2 rows of dice + top hint bar
    const arenaTopOffset = 28;
    const arenaPlayHeight = Math.max(dieSize * 2 + 34, isMaxi ? 236 : 210);
    const arenaHeight = arenaTopOffset + arenaPlayHeight;
    const totalHeight = arenaHeight + trayHeight;

    // Compute collision-free positions in the upper Rolling Arena for all `count` dice
    const rawArenaPositions = computeDicePositions({
      count,
      width: boardWidth,
      height: arenaPlayHeight,
      dieSize,
      jitter: 0.28,
    });

    const arenaPositions = rawArenaPositions.map(p => ({
      x: p.x,
      y: arenaTopOffset + p.y,
    }));

    // Compute exact centers of the `count` numbered slots in the Keep Tray
    const slotY = arenaHeight + trayHeaderH + traySlotsAreaH / 2;
    const slotPositions = Array.from({ length: count }, (_, i) => ({
      x: slotPaddingX + (i + 0.5) * slotStep,
      y: slotY,
    }));

    return {
      dieSize,
      arenaHeight,
      trayHeight,
      totalHeight,
      arenaPositions,
      slotPositions,
      slotSocketSize: dieSize + 4,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardWidth, count, isMaxi, rollSeed]);

  const lockedCount = dice.filter(d => d.isLocked).length;
  const hasRolled = dice.some(d => d.value > 0);
  const quickOptions = useMemo(
    () => (canInteract && hasRolled ? computeQuickKeepOptions(dice) : []),
    [canInteract, hasRolled, dice]
  );

  const applyLockedSet = useCallback(
    (targetIds: number[]) => {
      if (!canInteract) return;
      const targetSet = new Set(targetIds);
      if (onSetLockedIds) {
        onSetLockedIds(targetIds);
        return;
      }
      // Fallback: toggle each die whose state differs from targetSet
      for (const d of dice) {
        const shouldLock = targetSet.has(d.id);
        if (Boolean(d.isLocked) !== shouldLock) {
          onToggleDie(d.id);
        }
      }
    },
    [canInteract, dice, onSetLockedIds, onToggleDie]
  );

  const presetList: Array<{ id: TablePresetId; icon: string; name: string; desc: string }> = [
    {
      id: 'auto',
      icon: '✨',
      name: locale === 'en' ? 'Follow theme (Auto)' : 'Följ tema (Auto)',
      desc: locale === 'en' ? 'Adapts automatically to active theme' : 'Anpassas automatiskt efter valt tema',
    },
    ...Object.values(TABLE_PRESETS).map(p => ({
      id: p.id,
      icon: p.icon,
      name: locale === 'en' ? p.nameEn : p.nameSv,
      desc: locale === 'en' ? p.descEn : p.descSv,
    })),
  ];

  const currentPresetLabel =
    presetId === 'auto'
      ? `${resolvedPreset.icon} ${locale === 'en' ? resolvedPreset.nameEn : resolvedPreset.nameSv}`
      : `${resolvedPreset.icon} ${locale === 'en' ? resolvedPreset.nameEn : resolvedPreset.nameSv}`;

  return (
    <OuterFrame $preset={resolvedPreset}>
      <FrameHeaderBar $preset={resolvedPreset}>
        <BoardBrandBadge $preset={resolvedPreset}>
          <span aria-hidden="true">🎲</span>
          <span>
            {locale === 'en' ? '3D Raytraced Table' : '3D Raytracat Spelbord'}
          </span>
        </BoardBrandBadge>

        <PresetPickerWrapper ref={pickerRef}>
          <PresetTriggerBtn
            type="button"
            $preset={resolvedPreset}
            onClick={() => setPickerOpen(o => !o)}
            aria-label={locale === 'en' ? 'Choose game table style' : 'Välj spelbord'}
            aria-expanded={pickerOpen}
          >
            <span>{currentPresetLabel}</span>
            <span aria-hidden="true">▾</span>
          </PresetTriggerBtn>

          {pickerOpen && (
            <PresetDropdown $preset={resolvedPreset} role="menu">
              {presetList.map(item => (
                <PresetOptionBtn
                  key={item.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={presetId === item.id}
                  $active={presetId === item.id}
                  $accent={resolvedPreset.accentColor}
                  onClick={() => {
                    setSavedTablePresetId(item.id);
                    setPickerOpen(false);
                  }}
                >
                  <span style={{ fontSize: 16 }} aria-hidden="true">
                    {item.icon}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <strong style={{ fontSize: 12 }}>{item.name}</strong>
                    <span style={{ fontSize: 10.5, opacity: 0.75 }}>{item.desc}</span>
                  </span>
                </PresetOptionBtn>
              ))}
            </PresetDropdown>
          )}
        </PresetPickerWrapper>
      </FrameHeaderBar>

      <InnerTableWell ref={wellRef} $preset={resolvedPreset} $heightPx={layout.totalHeight}>
        <ArenaZoneHint $preset={resolvedPreset}>
          <span>
            {locale === 'en' ? '🎯 Rolling Arena' : '🎯 Kastyta'}
          </span>
          <span>
            {hasRolled
              ? locale === 'en'
                ? 'Click a die to keep / release'
                : 'Klicka på en tärning för att spara / släppa'
              : locale === 'en'
                ? 'Ready to roll'
                : 'Redo för kast'}
          </span>
        </ArenaZoneHint>

        {/* Illuminated Keep Tray at the bottom of the 3D table */}
        <KeepTrayDock
          $preset={resolvedPreset}
          $topPx={layout.arenaHeight}
          $heightPx={layout.trayHeight}
          $hasLocked={lockedCount > 0}
        >
          <KeepTrayHeader $preset={resolvedPreset}>
            <KeepTrayTitle $preset={resolvedPreset}>
              <span aria-hidden="true">🔒</span>
              <span>
                {locale === 'en'
                  ? `Keep Tray (${lockedCount}/${count})`
                  : `Spara-hylla (${lockedCount}/${count})`}
              </span>
            </KeepTrayTitle>
            <KeepTraySummary $preset={resolvedPreset}>
              {describeKeptPattern(dice, locale)}
            </KeepTraySummary>
          </KeepTrayHeader>
        </KeepTrayDock>

        {/* Numbered recessed sockets in the Keep Tray */}
        {dice.map((die, idx) => {
          const slot = layout.slotPositions[idx] || { x: 40 + idx * 70, y: layout.arenaHeight + 60 };
          return (
            <KeepSlotSocket
              key={`slot-${die.id}`}
              $preset={resolvedPreset}
              $x={slot.x}
              $y={slot.y}
              $size={layout.slotSocketSize}
              $isLocked={die.isLocked}
              $canInteract={canInteract && hasRolled}
              onClick={() => {
                if (canInteract && hasRolled) onToggleDie(die.id);
              }}
              aria-hidden="true"
            >
              <SlotNumberLabel $isLocked={die.isLocked}>#{idx + 1}</SlotNumberLabel>
            </KeepSlotSocket>
          );
        })}

        {/* Single set of [data-testid="die"] elements that smoothly glide between Arena and Keep Tray */}
        {dice.map((die, idx) => {
          const targetPos = die.isLocked
            ? layout.slotPositions[idx]
            : layout.arenaPositions[idx];
          const x = targetPos?.x ?? 50 + idx * 80;
          const y = targetPos?.y ?? 110;

          return (
            <PositionedDieSlot
              key={die.id}
              $x={x}
              $y={y}
              $rolling={isRolling && !die.isLocked}
              onPointerDown={() => onPointerDownDie?.(die.id)}
              onPointerUp={() => onPointerUpDie?.(die.id)}
              onPointerLeave={() => onPointerLeaveDie?.(die.id)}
              onContextMenu={e => e.preventDefault()}
            >
              <Die2D
                value={die.value}
                isLocked={die.isLocked}
                onClick={() => onToggleDie(die.id)}
                isRolling={isRolling && !die.isLocked}
                variant={layout.dieSize <= 58 ? 'mini' : layout.dieSize <= 74 ? 'compact' : 'normal'}
                index={idx}
                material={resolvedPreset.dieMaterial}
                sizePx={layout.dieSize}
                showShortcut={hasRolled}
              />
            </PositionedDieSlot>
          );
        })}

        {overlay}
      </InnerTableWell>

      {/* Quick-Keep Bar for effortless 1-click selection of pairs, triples, straights, etc. */}
      {canInteract && hasRolled && !isRolling && (
        <QuickKeepBar
          $preset={resolvedPreset}
          role="group"
          aria-label={locale === 'en' ? 'Quick keep dice' : 'Snabbval för att spara tärningar'}
        >
          {quickOptions.map(opt => {
            const isExactlySelected =
              opt.targetLockedIds.length === lockedCount &&
              opt.targetLockedIds.every(id => dice.find(d => d.id === id)?.isLocked);
            return (
              <QuickKeepPill
                key={opt.id}
                type="button"
                $preset={resolvedPreset}
                $active={isExactlySelected}
                onClick={() => applyLockedSet(isExactlySelected ? [] : opt.targetLockedIds)}
              >
                <span>✨ {locale === 'en' ? opt.labelEn : opt.labelSv}</span>
              </QuickKeepPill>
            );
          })}

          {lockedCount < count && (
            <QuickKeepPill
              type="button"
              $preset={resolvedPreset}
              onClick={() => applyLockedSet(dice.map(d => d.id))}
            >
              <span>🔒 {locale === 'en' ? 'Keep all' : 'Spara alla'}</span>
            </QuickKeepPill>
          )}

          {lockedCount > 0 && (
            <QuickKeepPill
              type="button"
              $preset={resolvedPreset}
              onClick={() => applyLockedSet([])}
            >
              <span>🔓 {locale === 'en' ? 'Release all' : 'Släpp alla'}</span>
            </QuickKeepPill>
          )}
        </QuickKeepBar>
      )}
    </OuterFrame>
  );
};

export default DiceBoard3D;
